import { useCallback, useRef, useState } from 'react';
import { interviewRuntimeAPI } from '../api/interviewRuntimeAPI';

/** In preference order. WebsiteInterviewPage.js:959 hardcodes
 * 'audio/webm', which throws a NotSupportedError constructing MediaRecorder
 * on Safari (no webm support there) - feature-detected here instead, per
 * the plan's own note on that pattern. */
const CANDIDATE_MIME_TYPES = ['audio/webm', 'audio/mp4', 'audio/ogg'];

function pickSupportedMimeType() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return undefined; // let the browser choose its own default
  }
  return CANDIDATE_MIME_TYPES.find((t) => MediaRecorder.isTypeSupported(t));
}

/**
 * Record -> transcribe -> hand back text, mirroring
 * WebsiteInterviewPage.js:950-1030's startOpenAIRecording /
 * stopOpenAIRecordingAndTranscribe, which already chose MediaRecorder +
 * Whisper over the browser's SpeechRecognition API for dedicated interview
 * tools. Kept as one hook rather than that file's two top-level functions
 * plus a ref, so a caller gets start/stop/isRecording/isTranscribing
 * without owning any of the MediaRecorder plumbing itself.
 */
export function useVoiceRecording() {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState('');
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const start = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Microphone not available in this browser.');
      return false;
    }
    setError('');
    try {
      // Asked for explicitly rather than left to each browser's default: the
      // interviewer's voice plays through the same device.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      const mimeType = pickSupportedMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      return true;
    } catch {
      setError('Could not access the microphone. Check permissions.');
      return false;
    }
  }, []);

  /** Resolves with the transcript, or null if nothing usable came back —
   * never throws, so a caller can always fall back to the typed box rather
   * than crash the turn on a bad recording. */
  const stop = useCallback(() => new Promise((resolve) => {
    const recorder = recorderRef.current;
    if (!recorder) { resolve(null); return; }

    recorder.onstop = async () => {
      const stream = recorder.stream;
      const mimeType = recorder.mimeType || 'audio/webm';
      setIsRecording(false);
      setIsTranscribing(true);
      try {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];
        const result = await interviewRuntimeAPI.transcribeAudioBlob(blob);
        const text = (result?.text || '').trim();
        if (!result?.success || !text) {
          setError('No speech detected — you can type your answer instead.');
          resolve(null);
          return;
        }
        resolve(text);
      } catch {
        setError('Could not transcribe that — you can type your answer instead.');
        resolve(null);
      } finally {
        setIsTranscribing(false);
        stream?.getTracks().forEach((t) => t.stop());
        recorderRef.current = null;
      }
    };

    try {
      recorder.stop();
    } catch {
      setIsRecording(false);
      resolve(null);
    }
  }), []);

  return { start, stop, isRecording, isTranscribing, error };
}
